/* eslint-disable react/no-multi-comp */
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { SetRequestParamsType } from '@/utils/hooks/swr/reports/useFetchReports';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';
// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';

import PropTypes from 'prop-types';

import { getSortOptions, SortOptionType } from '@/lib/posts/postSortOptions';
// Lib
import { INSTAGRAM } from '@/lib/socialProfile';

import Select from '@/components/base/Select';
// Components

const SelectSortByButton = ({ selected }: { selected: SortOptionType }) => {
    return (
        <Select.Button sizeStyle="sm" baseStyle="noBorder" selected={selected}>
            <span className="block truncate text-xs text-icoBlack dark:text-white">
                {selected?.label ?? selected?.value}
            </span>
        </Select.Button>
    );
};

type SelectSortByProps = {
    setRequestParams: SetRequestParamsType;
    postType: string;
};

export const SelectSortBy = ({ setRequestParams, postType }: SelectSortByProps) => {
    const { t } = useTranslation('commons');
    const { activeSocialProfile } = useActiveSocialProfile();
    const { socialProfiles } = useSocialProfiles();
    const sortOptions = useMemo(() => getSortOptions(postType, t), [postType, t]);
    const router = useRouter();
    const { deleteQuery } = useRouterQuery();
    const { column, order, socialProfileId } = router.query;

    // Determine network from URL or active profile
    const network = useMemo(() => {
        if (socialProfileId) {
            const socialProfile = socialProfiles.find((sp) => sp.id === Number(socialProfileId));
            return socialProfile?.social_network_type ?? INSTAGRAM;
        }
        return activeSocialProfile?.social_network_type ?? INSTAGRAM;
    }, [socialProfileId, socialProfiles, activeSocialProfile]);

    const getDefaultSort = () => {
        return (
            sortOptions[network]?.[0] ?? {
                label: t('sort.by_most_recent'),
                label_selected: t('sort.by_most_recent'),
                value: 'created',
                order: 'desc'
            }
        );
    };

    const [activeSort, setActiveSort] = useState(
        column && order
            ? sortOptions[network]?.filter(
                  (element) => element.value === column && element.order === order
              )?.[0]
            : getDefaultSort()
    );

    const queryParamsApplied = useMemo(() => {
        if (!column || !order || !activeSort) {
            return false;
        }
        const spIsApplied = socialProfileId && Number(socialProfileId) === activeSocialProfile?.id;
        return (
            spIsApplied &&
            column &&
            order &&
            column === activeSort.value &&
            order === activeSort.order
        );
    }, [column, order, activeSort, socialProfileId, activeSocialProfile]);

    // Sync activeSort when network changes
    useEffect(() => {
        if (!activeSort) {
            return;
        }

        // Check if current activeSort exists in the new network options
        const currentSortExistsInNetwork = sortOptions[network]?.some(
            (option) => option.value === activeSort.value && option.order === activeSort.order
        );

        // Only reset to default if current sort doesn't exist in new network
        if (!currentSortExistsInNetwork) {
            if (!sortOptions[network]?.length) {
                setActiveSort(null);
                return;
            }
            const defaultSort = getDefaultSort();
            if (
                defaultSort?.value !== activeSort.value ||
                defaultSort?.order !== activeSort.order
            ) {
                setActiveSort(defaultSort);
            }
        }
    }, [network, sortOptions, activeSort]);

    useEffect(() => {
        if (!activeSort) {
            return;
        }
        if (queryParamsApplied) {
            const newUrl = deleteQuery(['column', 'order', 'socialProfileId']);
            router.replace(newUrl);
        }
        setRequestParams((prevState) => ({
            ...prevState,
            sort: {
                column: activeSort.value,
                order: activeSort.order
            }
        }));
    }, [activeSort]);

    if (!activeSort) {
        return null;
    }

    return (
        <Select
            selected={activeSort}
            setSelected={setActiveSort}
            options={sortOptions[network]}
            noBorder
            button={<SelectSortByButton selected={activeSort} />}
        />
    );
};

SelectSortBy.propTypes = {
    setRequestParams: PropTypes.func,
    postType: PropTypes.string
};

export default SelectSortBy;
